import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dynamo, LambdaInvocationTypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import { appAPI } from '../../utils/api';
import { getUsageMetricTypeCacheKey } from './cacheKeys';
import { getFastenRefreshHistoryCacheKey } from './useFastenRefreshHistory';

export type RefreshStatusResponse = {
    refreshProgress: Dynamo.FastenRefreshProgress | null;
    canRefresh: boolean;
    retryAfterSeconds?: number;
};

export type UseFastenRefreshArgs = {
    medicalProfileId: string;
    orgConnectionId: string;
    enabled?: boolean;
    /** Poll interval in milliseconds when refresh is in progress */
    refetchInterval?: number;
    /** Callback when refresh is successfully triggered */
    onRefreshSuccess?: () => void;
    /** Callback when refresh fails */
    onRefreshError?: (error: Error) => void;
};

export type UseFastenRefreshResult = {
    /** Whether the refresh status is loading */
    isLoading: boolean;
    /** Current refresh progress */
    refreshProgress: Dynamo.FastenRefreshProgress | null;
    /** Whether the user can trigger a refresh (rate limit check) */
    canRefresh: boolean;
    /** Seconds until refresh is available again (if rate limited) */
    retryAfterSeconds?: number;
    /** Whether a refresh is currently in progress */
    isRefreshing: boolean;
    /** Trigger a manual refresh */
    triggerRefresh: () => void;
    /** Whether the refresh trigger mutation is pending */
    isRefreshPending: boolean;
    /** Refetch the refresh status */
    refetchStatus: () => void;
};

const useFastenRefresh = (args: UseFastenRefreshArgs): UseFastenRefreshResult => {
    const { medicalProfileId, orgConnectionId, refetchInterval, onRefreshSuccess, onRefreshError } = args;
    const enabled = args?.enabled !== false;
    const queryClient = useQueryClient();

    // Query for refresh status
    const { data, isLoading, refetch } = useQuery({
        queryKey: ['fasten-refresh-status', medicalProfileId, orgConnectionId],
        queryFn: async () => {
            const response = await appAPI.get<RefreshStatusResponse>(
                urls.api.ehrIntegrations.fasten.endpoints.connections.refreshStatus(medicalProfileId, orgConnectionId),
            );
            return response;
        },
        enabled: enabled && !!medicalProfileId && !!orgConnectionId,
        staleTime: 1000 * 30, // 30 seconds
        refetchInterval:
            refetchInterval ||
            ((query) => (query.state.data?.refreshProgress?.status === 'in-progress' ? 5000 : false)),
    });

    // Mutation to trigger refresh
    const refreshMutation = useMutation({
        mutationFn: async () => {
            const response = await appAPI.post<LambdaInvocationTypes.RefreshFastenConnectionResponse>(
                urls.api.ehrIntegrations.fasten.endpoints.connections.refresh(medicalProfileId, orgConnectionId),
            );
            return response;
        },
        onSuccess: () => {
            // Invalidate queries to refresh data
            queryClient.invalidateQueries({ queryKey: ['fasten-refresh-status', medicalProfileId, orgConnectionId] });
            queryClient.invalidateQueries({ queryKey: ['fasten-connections', medicalProfileId] });
            queryClient.invalidateQueries({ queryKey: getUsageMetricTypeCacheKey(medicalProfileId, 'fasten-refresh') });
            queryClient.invalidateQueries({
                queryKey: getFastenRefreshHistoryCacheKey(medicalProfileId, orgConnectionId),
            });
            onRefreshSuccess?.();
        },
        onError: (error: Error) => {
            onRefreshError?.(error);
        },
    });

    const isRefreshing = data?.refreshProgress?.status === 'in-progress';

    return {
        isLoading,
        refreshProgress: data?.refreshProgress || null,
        canRefresh: data?.canRefresh ?? false,
        retryAfterSeconds: data?.retryAfterSeconds,
        isRefreshing,
        triggerRefresh: () => refreshMutation.mutate(),
        isRefreshPending: refreshMutation.isPending,
        refetchStatus: refetch,
    };
};

export default useFastenRefresh;
